import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { resolveApiPath } from "@/lib/utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import * as Sentry from "@sentry/react";
import { useState } from "react";

type AudioPlayerComponentData = Extract<ArtifactComponent, { type: "audioPlayer" }>;

/**
 * Inline audio for a first-party asset — today, phone-call recordings.
 *
 * Renders the native control rather than a custom transport: it already seeks
 * via range requests, which is the whole point of the proxy behind it. A failed
 * load degrades to a line of text rather than a dead control, and reports
 * itself, since a systematic failure (a CSP block, a content-type mismatch)
 * would otherwise surface only as user complaints.
 */
export const AudioPlayerBlock = ({ component }: { component: AudioPlayerComponentData }) => {
  const { isSharedThread } = useArtifactViewCtx();
  // Keyed by src rather than a bare boolean so a re-render carrying different
  // audio gets a fresh attempt — this block stays mounted across artifact
  // updates, and a latched failure would outlive the URL that caused it.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  // Unlike images, recordings have no share-token-scoped public route, so a
  // shared-thread viewer cannot be authorized for one at all.
  if (component.requiresAuth && isSharedThread) return null;

  const src = resolveApiPath(component.src);

  return (
    <div className="flex flex-col gap-2">
      {component.heading ? (
        <div className="text-sm @md:text-base text-foreground font-semibold">
          {component.heading}
        </div>
      ) : null}
      {failedSrc === src ? (
        <output className="text-sm text-muted-foreground">Recording unavailable</output>
      ) : (
        <audio
          controls
          // Metadata only: a recording is several hundred KB and most cards are
          // scrolled past, so the bytes wait until the user presses play.
          preload="metadata"
          src={src}
          aria-label={component.label}
          onError={(event) => {
            const error = event.currentTarget.error;
            Sentry.captureException(new Error("Call recording failed to load"), {
              extra: { src, code: error?.code, message: error?.message },
            });
            setFailedSrc(src);
          }}
          className="w-full"
        >
          {/* Vapi ships no caption file. When the call produced an exchange the
              card renders its transcript directly below; a voicemail has none. */}
          <track kind="captions" />
        </audio>
      )}
    </div>
  );
};
